import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ExtractedSection, SlotSurfaceForms } from '../onboarding-api';

export interface AsyncJobData {
  _id: string;
  type: 'generic' | 'manual' | 'route';
  status: 'pending' | 'processing' | 'enhancing' | 'completed' | 'failed' | 'delayed' | 'canceled';
  description: string;
  progress: number; // 0-100
  context: {
    operationType?: 'extract_rules' | 'save_rules' | 'clone_playbook';
    playbookId?: string;
    playbookName?: string;
    totalRules?: number;
    // extract_rules-specific fields, set by the Node proxy at job creation
    // (see onboarding-router.mjs's /extract-rules/stream handler).
    paperType?: 'first_party' | 'third_party';
    contractType?: string;
    filenames?: Array<string>;
    totalFiles?: number;
  };
  meta?: {
    current?: number;
    total?: number;
    message?: string;
  };
  result?: {
    playbookId?: string;
    rulesAdded?: number;
    rulesSkipped?: number;
    clonedRules?: number;
    name?: string;
    error?: string;
    skippedRules?: Array<string>;
    // extract_rules-specific fields, written directly by bbcedit on
    // completion/failure (see extraction.py's _mirror_job calls).
    sections?: Array<ExtractedSection>;
    slot_surface_forms?: SlotSurfaceForms;
  };
  failureReason?: string;
  audit: {
    createdBy: string;
    createdDate: Date;
    modifiedDate?: Date;
  };
}

export interface UseAsyncJobPollingOptions {
  jobId: string | null;
  onComplete?: (result: AsyncJobData['result']) => void;
  onError?: (error: string) => void;
  pollInterval?: number;
  enabled?: boolean;
}

/**
 * Hook to poll an async job for progress updates.
 * Automatically stops polling when job completes, fails, or is canceled.
 *
 * @example
 * const { job, isPolling, percentComplete, message } = useAsyncJobPolling(bbAxios, {
 *   jobId: activeJobId,
 *   onComplete: (result) => {
 *     toast.success('Operation completed!');
 *     refreshData();
 *   },
 *   onError: (error) => {
 *     toast.error(`Failed: ${error}`);
 *   },
 * });
 */
export function useAsyncJobPolling(bbAxios: AxiosInstance, options: UseAsyncJobPollingOptions) {
  const { jobId, onComplete, onError, pollInterval = 2000, enabled = true } = options;
  const [job, setJob] = useState<AsyncJobData | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  const fetchJob = useCallback(async () => {
    if (!jobId || !enabled) {
      return;
    }

    try {
      const { data } = await bbAxios.get<AsyncJobData>(`/api/v2/async-jobs/${jobId}`);

      if (!mountedRef.current) {
        return;
      }

      setJob(data);

      if (data.status === 'completed') {
        setIsPolling(false);
        onCompleteRef.current?.(data.result);
      } else if (data.status === 'failed') {
        setIsPolling(false);
        onErrorRef.current?.(data.result?.error || data.failureReason || 'Operation failed');
      } else if (data.status === 'canceled') {
        setIsPolling(false);
        onErrorRef.current?.('Operation was canceled');
      } else {
        // Continue polling for pending/processing/enhancing/delayed
        timeoutRef.current = setTimeout(fetchJob, pollInterval);
      }
    } catch (err: unknown) {
      console.error('Job polling error:', err);

      if (!mountedRef.current) {
        return;
      }

      const axiosError = err as { response?: { status: number } };
      if (axiosError.response?.status === 404) {
        setIsPolling(false);
        onErrorRef.current?.('Job not found');
      } else if (axiosError.response?.status === 401) {
        setIsPolling(false);
        onErrorRef.current?.('Unauthorized - please log in again');
      } else {
        // Retry on network errors
        timeoutRef.current = setTimeout(fetchJob, pollInterval);
      }
    }
  }, [jobId, bbAxios, pollInterval, enabled]);

  // Start polling when jobId changes
  useEffect(() => {
    mountedRef.current = true;

    if (jobId && enabled) {
      setIsPolling(true);
      fetchJob();
    } else {
      setIsPolling(false);
      setJob(null);
    }

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [jobId, enabled, fetchJob]);

  const cancelPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  return {
    job,
    isPolling,
    cancelPolling,
    percentComplete: job?.progress || 0,
    message: job?.meta?.message || job?.description || '',
    current: job?.meta?.current || 0,
    total: job?.meta?.total || 0,
    status: job?.status,
  };
}
